import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Star, Scissors, Shield, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { IMAGES } from '../utils/images';
import { ARTICLES } from '../utils/blogPosts';
import { useSEO } from '../utils/seo';
import { useCopy, useLocale } from '../i18n/LocaleContext';

// Article du journal qui raconte le nom de la marque (lien masqué s'il est dépublié).
const nameArticle = ARTICLES.find(article => article.slug === 'le-placard-d-ou-vient-le-nom');

const ICONS = [Heart, Shield, Scissors, Star];

const COPY = {
  fr: {
    seoTitle: 'Notre Histoire — Le Placard à Doudous',
    seoDescription: 'Découvrez l’histoire de Margot et de son atelier artisanal. Des marque-pages en tissu faits main en France avec amour et savoir-faire.',
    steps: [
      { num: '01', title: 'Choix du minkee', desc: 'Margot sélectionne rigoureusement chaque rouleau de minkee chez des fournisseurs européens certifiés. Seul le meilleur minkee à poil court passe le test de douceur.' },
      { num: '02', title: 'Patron & Découpe', desc: 'Chaque ami tout doux est découpé à la main sur ses patrons originaux, créés et perfectionnés au fil des années dans l’atelier.' },
      { num: '03', title: 'Couture & Broderies', desc: 'Cousu à la machine avec amour, puis chaque détail, comme les yeux brodés, est réalisé à la main au fil patiemment choisi.' },
      { num: '04', title: 'Contrôle & Emballage', desc: 'Chaque pièce est vérifiée, photographiée et enveloppée dans du papier de soie. Un petit mot de Margot accompagne chaque commande.' },
    ],
    values: [
      { title: 'Avec amour', desc: 'Chaque pièce est unique, faite à la main, avec une attention obsessionnelle aux détails.' },
      { title: 'Matières nobles', desc: 'Seuls le minkee premium, certifié, ultra-doux et lavable, et le coton Tana Lawn de Liberty Fabrics entrent dans l’atelier.' },
      { title: 'Savoir-faire', desc: 'Chaque patron a été créé, testé et perfectionné pour une finition irréprochable.' },
      { title: 'Made in France', desc: 'Cousu en France, expédié depuis l’atelier de Margot, enveloppé de soin.' },
    ],
    eyebrow: 'L’atelier de Margot', title: 'Le rêve de Margot', titleAccent: 'est devenu votre douceur',
    intro: 'Un placard entier de marque-pages en tissu, cousus un à un, qui se glissent aussi bien dans un livre que dans une poche et vous accompagnent partout avec douceur.',
    pills: ['Pièces uniques', 'Cousues main en France', 'Expédition soignée'],
    heroAlt: 'Le placard rempli de marque-pages en tissu', workbenchAlt: 'Le plan de travail de l’atelier', libertyAlt: 'Coupons de tissus Liberty',
    storyEyebrow: 'L’enfant qui voulait des peluches', storyTitle: 'Des carnets de croquis à l’aiguille',
    story: [
      'Depuis toute petite, Margot passe ses soirées à dessiner des peluches imaginaires. Renards, lapins, licornes — des carnets entiers remplis de créatures douces que personne ne fabriquait encore comme elle les rêvait.',
      'Des années plus tard, lectrice passionnée, elle pose son roman ouvert sur la table — ce geste terrible qui fracture les dos. Et l’évidence la frappe : et si ses mini-peluches servaient de marque-pages ?',
      'Elle sort sa machine à coudre. Elle commande son premier rouleau de minkee. Elle coud. Elle recommence. Elle perfectionne. Et Sylvette la Crevette naît.',
    ],
    quote: 'Je voulais qu’on ait hâte de rouvrir son livre, juste pour retrouver son compagnon.', quoteAuthor: 'Margot',
    nameLink: 'Pourquoi « Le Placard à Doudous » ? Lire l’histoire du nom',
    valuesEyebrow: 'Ce qui ne change jamais', valuesTitle: 'Quatre promesses cousues dans chaque pièce',
    stepsEyebrow: 'Coulisses de l’atelier', stepsTitle: 'De la matière à la magie', stepsIntro: 'Quatre gestes, dans l’ordre, sans raccourci. C’est tout l’atelier.',
    ctaEyebrow: 'Le placard vous attend', ctaTitle: 'Prêt(e) à adopter votre ami tout doux ?',
    ctaText: 'Découvrez toute la boutique et trouvez le compagnon de lecture qui vous ressemble.', ctaButton: 'Voir la boutique',
  },
  en: {
    seoTitle: 'Our Story — Le Placard à Doudous',
    seoDescription: 'Discover the story of Margot and her craft workshop. Fabric bookmarks handmade in France with love and skill.',
    steps: [
      { num: '01', title: 'Choosing the minky', desc: 'Margot carefully selects every roll of minky from certified European suppliers. Only the finest short-pile minky passes the softness test.' },
      { num: '02', title: 'Pattern & Cutting', desc: 'Each cuddly friend is cut by hand from its original patterns, designed and refined over the years in the workshop.' },
      { num: '03', title: 'Sewing & Embroidery', desc: 'Machine-sewn with love, then every detail, like the embroidered eyes, is finished by hand with carefully chosen thread.' },
      { num: '04', title: 'Checking & Wrapping', desc: 'Each piece is checked, photographed and wrapped in tissue paper. A little note from Margot comes with every order.' },
    ],
    values: [
      { title: 'With love', desc: 'Each piece is one of a kind, made by hand, with an obsessive attention to detail.' },
      { title: 'Fine fabrics', desc: 'Only premium certified minky, ultra-soft and washable, and Liberty Fabrics Tana Lawn cotton make it into the workshop.' },
      { title: 'Craftsmanship', desc: 'Every pattern has been designed, tested and refined for a flawless finish.' },
      { title: 'Made in France', desc: 'Sewn in France, shipped from Margot’s workshop, wrapped with care.' },
    ],
    eyebrow: 'Margot’s workshop', title: 'Margot’s dream', titleAccent: 'has become your little comfort',
    intro: 'A whole cupboard of fabric bookmarks, sewn one by one, that slip into a book as easily as into a pocket and keep you soft company wherever you go.',
    pills: ['One-of-a-kind pieces', 'Sewn by hand in France', 'Carefully packed'],
    heroAlt: 'The cupboard filled with fabric bookmarks', workbenchAlt: 'The workshop workbench', libertyAlt: 'Liberty fabric remnants',
    storyEyebrow: 'The little girl who wanted plushies', storyTitle: 'From sketchbooks to the needle',
    story: [
      'Ever since she was little, Margot has spent her evenings drawing imaginary plushies. Foxes, rabbits, unicorns — whole sketchbooks filled with soft creatures that nobody made quite the way she dreamed them.',
      'Years later, a passionate reader, she lays her novel face down on the table — that terrible gesture that breaks spines. And it hits her: what if her mini plushies could be bookmarks?',
      'She gets out her sewing machine. She orders her first roll of minky. She sews. She starts again. She refines. And Sylvette the Shrimp is born.',
    ],
    quote: 'I wanted people to look forward to opening their book again, just to find their companion.', quoteAuthor: 'Margot',
    nameLink: 'Why “Le Placard à Doudous”? Read the story behind the name',
    valuesEyebrow: 'What never changes', valuesTitle: 'Four promises sewn into every piece',
    stepsEyebrow: 'Behind the scenes', stepsTitle: 'From fabric to magic', stepsIntro: 'Four steps, in order, no shortcuts. That’s the whole workshop.',
    ctaEyebrow: 'The cupboard is waiting for you', ctaTitle: 'Ready to adopt your cuddly friend?',
    ctaText: 'Browse the whole shop and find the reading companion that feels like you.', ctaButton: 'Visit the shop',
  },
};

function LazyImg({ src, alt, className, imgClassName }) {
  const [loaded, setLoaded] = React.useState(false);
  // Le positionnement de l'appelant (absolute/fixed/sticky) prime ; sinon relative par défaut
  // pour que le placeholder et l'image remplissent la zone.
  const hasPosition = /(^|\s)(absolute|fixed|sticky)(\s|$)/.test(className || '');
  return (
    <div className={`overflow-hidden ${hasPosition ? '' : 'relative'} ${className || ''}`}>
      {!loaded && <div className="absolute inset-0 bg-[hsl(var(--muted))] animate-pulse" />}
      <img src={src} alt={alt} onLoad={() => setLoaded(true)}
        className={`w-full h-full object-cover transition-opacity duration-700 ${loaded ? 'opacity-100' : 'opacity-0'} ${imgClassName || ''}`} />
    </div>
  );
}

export default function About() {
  const c = useCopy(COPY);
  const { lp } = useLocale();
  useSEO(c.seoTitle, c.seoDescription);
  return (
    <div className="min-h-screen pt-20">

      {/* ═══ 01 · HERO — split éditorial ═══ */}
      <section className="grid min-h-[calc(100vh-5rem)] max-h-[840px] grid-cols-1 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="relative flex flex-col justify-center overflow-hidden px-6 py-14 sm:px-10 lg:px-[clamp(1.75rem,6vw,5.5rem)]">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
            <p className="relative z-10 mb-6"><span className="kicker">{c.eyebrow}</span></p>
            <h1 className="relative z-10 max-w-[14ch] font-serif text-[clamp(2.5rem,4.6vw,4.1rem)] leading-[1.03] tracking-[-0.01em] text-foreground">
              {c.title} <i className="font-medium text-primary">{c.titleAccent}</i>
            </h1>
            <p className="mt-5 max-w-[46ch] font-sans text-sm leading-[1.8] text-muted-foreground">
              {c.intro}
            </p>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {c.pills.map(label => (
                <span key={label} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 font-sans text-xs font-semibold text-foreground shadow-soft">
                  <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand-rose-dust))]" aria-hidden="true" />
                  {label}
                </span>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="relative min-h-[340px] overflow-hidden lg:order-2">
          <LazyImg src={IMAGES.aboutHero} alt={c.heroAlt} className="absolute inset-0" />
          <div className="pointer-events-none absolute inset-5 rounded-3xl border border-white/50" aria-hidden="true" />
        </div>
      </section>

      {/* ═══ 02 · HISTOIRE ═══ */}
      <section className="mx-auto max-w-7xl px-4 pb-4 pt-24 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[0.92fr_1.08fr] lg:gap-[clamp(2.5rem,6vw,5.25rem)]">
          <div className="relative pb-14">
            <LazyImg src={IMAGES.atelierMargot} alt={c.workbenchAlt} className="aspect-[3/4] w-[88%] rounded-[1.75rem] shadow-ambient" />
            <LazyImg src={IMAGES.atelierDetails} alt={c.libertyAlt} className="absolute bottom-0 right-0 aspect-square w-[52%] rounded-[1.25rem] border-[6px] border-[hsl(var(--background))] shadow-ambient" />
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <p className="eyebrow mb-3">{c.storyEyebrow}</p>
            <h2 className="gold-hairline font-serif text-3xl text-foreground sm:text-4xl">{c.storyTitle}</h2>
            <div className="mt-6 space-y-4">
              <p className="dropcap font-sans leading-[1.9] text-[hsl(var(--muted-foreground))]">
                {c.story[0]}
              </p>
              <p className="font-sans leading-[1.9] text-[hsl(var(--muted-foreground))]">
                {c.story[1]}
              </p>
              <p className="font-sans leading-[1.9] text-[hsl(var(--muted-foreground))]">
                {c.story[2]}
              </p>
            </div>
            <blockquote className="relative mt-7 rounded-3xl border border-border bg-card px-8 pb-6 pt-8 shadow-soft">
              <span className="pointer-events-none absolute -top-4 left-5 font-serif text-[4.5rem] leading-none text-[hsl(var(--brand-rose-dust))] opacity-90" aria-hidden="true">«</span>
              <p className="font-serif text-xl text-foreground sm:text-2xl">{c.quote}</p>
              {/* Signature manuscrite de Margot sous sa citation. */}
              <cite className="script-touch mt-4 block text-[40px] not-italic">{c.quoteAuthor}</cite>
            </blockquote>
          </motion.div>
        </div>
        {/* Lien vers l'histoire du nom : sous les deux colonnes, sur toute la largeur. */}
        {nameArticle && (
          <Link to={lp(`/journal/${nameArticle.slug}`)} className="group mt-10 block w-full text-center font-sans text-base font-semibold leading-snug text-[hsl(var(--brand-fuchsia-ink))] sm:text-lg lg:mt-12">
            {c.nameLink}
            <ChevronRight className="ml-1 inline h-5 w-5 align-[-0.25em] transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
          </Link>
        )}
      </section>

      <hr className="stitch-line mx-auto mt-16 max-w-6xl" aria-hidden="true" />

      {/* ═══ 03 · VALEURS ═══ */}
      <section className="mx-auto max-w-6xl px-4 pt-16 sm:px-6 lg:px-10">
        <div className="relative mb-10">
          <p className="eyebrow mb-2.5">{c.valuesEyebrow}</p>
          <h2 className="gold-hairline font-serif text-3xl text-foreground sm:text-4xl">{c.valuesTitle}</h2>
        </div>
        <div className="grid grid-cols-2 gap-4 pb-16 lg:grid-cols-4 lg:gap-6">
          {c.values.map(({ title, desc }, i) => { const Icon = ICONS[i]; return (
            <motion.div key={title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}>
              <div className="group relative h-full overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-soft transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-ambient">
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-[hsl(var(--brand-rose))]/30">
                  <Icon className="h-4 w-4 text-[hsl(var(--brand-gold))]" strokeWidth={1.8} />
                </div>
                <p className="mb-1.5 font-serif text-[17px] text-foreground">{title}</p>
                <p className="font-sans text-xs leading-[1.7] text-muted-foreground">{desc}</p>
                <span className="absolute bottom-0 left-0 right-0 h-0.5 origin-left scale-x-0 bg-gradient-to-r from-primary to-transparent transition-transform duration-300 group-hover:scale-x-100" aria-hidden="true" />
              </div>
            </motion.div>
          ); })}
        </div>
      </section>


      {/* ═══ 05 · ÉTAPES ═══ */}
      <section className="mt-14 bg-[hsl(var(--muted))]/40 py-24">
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <p className="eyebrow mb-3">{c.stepsEyebrow}</p>
              <h2 className="gold-hairline font-serif text-3xl text-foreground sm:text-4xl">{c.stepsTitle}</h2>
            </motion.div>
            <p className="max-w-xs font-sans text-[13px] leading-relaxed text-muted-foreground">{c.stepsIntro}</p>
          </div>
          <div className="border-t border-border">
            {c.steps.map((step, i) => (
              <motion.div key={step.num} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}
                className="grid grid-cols-[70px_1fr] gap-x-6 border-b border-border px-2 py-7 transition-colors duration-300 hover:bg-[hsl(var(--card))]/60 sm:grid-cols-[110px_1fr_1.5fr] sm:gap-x-8">
                <span className="font-serif text-[2.5rem] leading-none text-[hsl(var(--brand-gold))]">{step.num}</span>
                <h3 className="font-serif text-xl text-foreground">
                  <span className="mr-3 inline-block h-2 w-2 rounded-full bg-[hsl(var(--brand-rose-dust))] align-[3px] opacity-65" aria-hidden="true" />
                  {step.title}
                </h3>
                <p className="mt-2 font-sans text-[13.5px] leading-[1.75] text-muted-foreground sm:col-start-3 sm:mt-0">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 06 · CTA ═══ */}
      <section className="relative overflow-hidden py-28 text-center">
        <div className="absolute left-1/2 top-1/2 h-[660px] w-[660px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(207,141,146,0.24)_0%,rgba(207,141,146,0)_68%)]" aria-hidden="true" />
        <div className="relative z-10 max-w-2xl mx-auto px-4">
          <p className="eyebrow mb-4">{c.ctaEyebrow}</p>
          <h2 className="mb-4 font-serif text-3xl text-foreground sm:text-[2.9rem]">{c.ctaTitle}</h2>
          <p className="mb-8 font-sans text-muted-foreground">{c.ctaText}</p>
          <Button asChild className="rounded-full bg-primary px-10 font-sans font-medium text-primary-foreground shadow-soft transition-[box-shadow,transform] duration-200 hover:shadow-ambient active:scale-[0.98]">
            <Link to={lp('/boutique')}>
              {c.ctaButton}
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
